import { Controller, FieldValues, useForm } from 'react-hook-form'
import {
  SelectItem,
  getSelectItemOptionLabel,
  normalizeSelectItems,
  isSelectItemEqual,
} from '../../../types/SelectItem'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  createFilterOptions,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { isValid } from 'date-fns'
import DateFnsProvider from '../../../providers/DateFnsProvider'
import {
  useAllItemTypeOptionsQuery,
  useAutoCompleteOptionsLazyQuery,
  useAutocompleteTitleOptionsLazyQuery,
  useCustomerAutocompleteOptionsLazyQuery,
  useItemFilterOptionsQuery,
  useLocationOptionsByFormQuery,
  useFormsForMuseumQuery,
} from '@kistill/client/data-access'
import { FilterControls } from './FilterControls'
import { ListViewContext } from '../../../contexts/ListViewContext'
import {
  handleOnSubmit,
  OnFilterType,
} from '../../../hooks/useHandleOnSubmitFilters'
import { useSelectedGuids } from '../../../contexts/SelectedGuidsContext'
import { useBlocker, useLocation } from 'react-router-dom'
import {
  getHiddenFilterFields,
  getFieldsForType,
} from '../filterConfig'
import { useClearFiltersOnColumnHide } from '../../../hooks/useClearFiltersOnColumnHide'
import { useFilterSync } from '../../../hooks/useFilterSync'

interface ItemFilterProps {
  onFilter: OnFilterType
  type?: string | undefined
  selected: readonly string[]
  setSelected: (selected: Set<string>) => void
  columnVisibility?: { [key: string]: boolean }
  initialFilterParams?: Record<string, unknown>
}

export function ItemFilters({
  onFilter,
  type,
  selected,
  setSelected,
  columnVisibility = {},
  initialFilterParams,
}: ItemFilterProps) {
  const { handleSubmit, watch, control, register, setValue, getValues, reset } =
    useForm({ shouldUnregister: true })
  const { showOnlySelected, updateShowOnlySelected } =
    useContext(ListViewContext)

  const [inputValueTitle, setInputValueTitle] = useState('')
  const [inputValueOrigin, setInputValueOrigin] = useState('')
  const [inputValueItemCollection, setInputValueItemCollection] = useState('')
  const [inputValueItem, setInputValueItem] = useState('')
  const [inputValueCustomer, setInputValueCustomer] = useState('')
  const [titleOptions, setTitleOptions] = useState<SelectItem[]>([])
  const [originOptions, setOriginOptions] = useState<SelectItem[]>([])
  const [itemCollectionOptions, setItemCollectionOptions] = useState<
    SelectItem[]
  >([])
  const [itemOptions, setItemOptions] = useState<SelectItem[]>([])
  const [customerOptions, setCustomerOptions] = useState<SelectItem[]>([])

  const { selectedGuidsSelectItems, source, setSelectedGuids } =
    useSelectedGuids()
  const location = useLocation()

  const { data: allTypeData } = useAllItemTypeOptionsQuery()
  const { data: itemFilterData } = useItemFilterOptionsQuery({
    variables: {
      code: type,
    },
  })
  const { data: locationsData } = useLocationOptionsByFormQuery({
    variables: {
      formCode: type,
    },
  })
  const { data: formsData } = useFormsForMuseumQuery({
    skip: type !== 'items',
    fetchPolicy: 'network-only',
  })
  const itemTypeOptions = itemFilterData?.itemTypeOptions as SelectItem[]
  const allTypeOptions = allTypeData?.allItemTypeOptions as SelectItem[]
  const locationsOptions = locationsData?.locationOptionsByForm as SelectItem[]
  const coldLocationsOptions =
    locationsData?.coldLocationOptionsByForm as SelectItem[]
  const languagesOptions = itemFilterData?.languageOptions as SelectItem[]
  const breiddOptions = itemFilterData?.breiddOptions as SelectItem[]
  const nedanmalstextarOptions = languagesOptions
  const formOptions: SelectItem[] =
    formsData?.formsForMuseum?.map((form) => ({
      id: form.code || form.guid,
      title: form.name,
    })) ?? []
  const hiddenFields = type
    ? getHiddenFilterFields(type, columnVisibility)
    : new Set<string>()

  // Only sync from parent on initial mount or when filters are cleared.
  const userActionRef = useRef(false)

  const [getTitleOptions] = useAutocompleteTitleOptionsLazyQuery({
    variables: { query: inputValueTitle },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data?.titleOptions) {
        setTitleOptions(data.titleOptions.slice())
      }
    },
  })

  const [getOriginNumberOptions] = useAutoCompleteOptionsLazyQuery({
    variables: { query: inputValueOrigin },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data.originNumberOptions) {
        setOriginOptions(
          data.originNumberOptions.map((x) => {
            return { id: x?.id, title: x.title }
          }),
        )
      }
    },
  })

  const [getItemCollectionNumberOptions] = useAutoCompleteOptionsLazyQuery({
    variables: { query: inputValueItemCollection },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data.itemCollectionNumberOptions) {
        setItemCollectionOptions(
          data.itemCollectionNumberOptions.map((x) => {
            return { id: x?.id, title: x.title }
          }),
        )
      }
    },
  })

  const [getItemNumberOptions] = useAutoCompleteOptionsLazyQuery({
    variables: { query: inputValueItem },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data.itemNumberOptions) {
        setItemOptions(
          data.itemNumberOptions.map((x) => {
            return { id: x?.id, title: x.title }
          }),
        )
      }
    },
  })

  const [getCustomerOptions] = useCustomerAutocompleteOptionsLazyQuery({
    variables: { query: inputValueCustomer },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data.customerOptions) {
        setCustomerOptions(
          data.customerOptions.map((x) => {
            return { id: x?.id, title: x.title }
          }),
        )
      }
    },
  })

  const onSubmit = useCallback(
    (data: FieldValues) => {
      handleOnSubmit(onFilter)(data)
    },
    [onFilter],
  )

  const { formStateRef, clearFilters: runClearFilters, resetSyncState, clearSpecificFields } = useFilterSync({
    initialFilterParams,
    reset,
    watch,
    onSubmit,
    getSyncKey: (normalized) => (normalized === null ? null : (type ?? null)),
    getClearSyncKey: () => type ?? null,
    shouldSkipSync: () => userActionRef.current,
    onEmptySync: () => {
      userActionRef.current = false
      setInputValueTitle('')
      setInputValueOrigin('')
      setInputValueItemCollection('')
      setInputValueItem('')
      setInputValueCustomer('')
    },
    onClear: () => {
      setInputValueTitle('')
      setInputValueOrigin('')
      setInputValueItemCollection('')
      setInputValueItem('')
      setInputValueCustomer('')
    },
  })

  useEffect(() => {
    resetSyncState()
    userActionRef.current = false
  }, [resetSyncState, type])

  useClearFiltersOnColumnHide(
    type,
    columnVisibility,
    setValue,
    clearSpecificFields,
  )
  const filterOptions = createFilterOptions<SelectItem>({
    trim: true,
  })

  const handleInputChangeTitle = (
    _event: React.SyntheticEvent,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueTitle(value)
  }

  const handleInputChangeOrigin = (
    _event: React.SyntheticEvent,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueOrigin(value)
  }

  const handleInputChangeItemCollection = (
    _event: React.SyntheticEvent,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueItemCollection(value)
  }

  const handleInputChangeItem = (
    _event: React.SyntheticEvent,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueItem(value)
  }

  const handleInputChangeCustomer = (
    _event: React.SyntheticEvent,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueCustomer(value)
  }

  const deselectCheckboxChange = () => {
    setSelected(new Set())
    if (showOnlySelected) {
      setValue('selectedGuids', null)
      updateShowOnlySelected(!showOnlySelected)
      userActionRef.current = true
      reset({})
      setInputValueTitle('')
      setInputValueOrigin('')
      setInputValueItemCollection('')
      setInputValueItem('')
      setInputValueCustomer('')
    }
  }

  function clearFilters() {
    userActionRef.current = true
    runClearFilters()
  }

  useEffect(() => {
    if (selectedGuidsSelectItems?.length > 0) {
      if (source === 'origin' && !getValues('originNumberSelectItem')?.length) {
        const matchedItems = selectedGuidsSelectItems.map((item) => ({
          id: item.id,
          title: item.title,
        }))

        setValue('originNumberSelectItem', matchedItems)
        setOriginOptions((prevOptions) => [...prevOptions, ...matchedItems])
        handleOnSubmit(onFilter)(getValues())
      } else if (
        source === 'itemCollection' &&
        !getValues('itemCollectionNumberSelectItem')?.length
      ) {
        const matchedItems = selectedGuidsSelectItems.map((item) => ({
          id: item.id,
          title: item.title,
        }))

        setValue('itemCollectionNumberSelectItem', matchedItems)
        setItemCollectionOptions((prevOptions) => [
          ...prevOptions,
          ...matchedItems,
        ])
        handleOnSubmit(onFilter)(getValues())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGuidsSelectItems, source])

  useEffect(() => {
    getOriginNumberOptions()
  }, [getOriginNumberOptions])

  useEffect(() => {
    getItemCollectionNumberOptions()
  }, [getItemCollectionNumberOptions])

  useEffect(() => {
    getItemNumberOptions()
  }, [getItemNumberOptions])

  useEffect(() => {
    getTitleOptions()
  }, [getTitleOptions])

  // Debounced query for customer autocomplete as user types
  useEffect(() => {
    if (inputValueCustomer.trim().length < 2) return
    const handle = setTimeout(() => {
      getCustomerOptions({ variables: { query: inputValueCustomer } })
    }, 250)
    return () => clearTimeout(handle)
  }, [inputValueCustomer, getCustomerOptions])

  useBlocker(({ nextLocation }) => {
    if (
      selectedGuidsSelectItems.length > 0 &&
      nextLocation.pathname !== location.pathname
    ) {
      setSelectedGuids([])
    }
    return false
  })

  const fieldsForType = getFieldsForType(type)
  const shouldShowField = (fieldName: string) => {
    return fieldsForType.includes(fieldName) && !hiddenFields.has(fieldName)
  }

  const renderAutocomplete = (
    name: string,
    label: string,
    options: SelectItem[],
    md = 2,
    inputValue?: string,
    onInputChange?: (
      event: React.SyntheticEvent,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => void,
    clearOnBlur = false,
  ) => {
    if (hiddenFields.has(name)) return null

    return (
      <Grid item xs={12} md={md} pr={0.25} key={name}>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              multiple
              size='small'
              options={normalizeSelectItems(options || [])}
              getOptionLabel={getSelectItemOptionLabel}
              isOptionEqualToValue={isSelectItemEqual}
              onChange={(e, data) => {
                const next = normalizeSelectItems(data)
                onChange(next)
                formStateRef.current = { ...formStateRef.current, [name]: next }
                userActionRef.current = true
                setTimeout(
                  () => handleOnSubmit(onFilter)(formStateRef.current),
                  0,
                )
              }}
              value={value || []}
              inputValue={inputValue}
              onInputChange={onInputChange}
              clearOnBlur={clearOnBlur}
              filterOptions={filterOptions}
              renderOption={(props, option) => (
                <li {...props} key={(option as SelectItem).id}>
                  {getSelectItemOptionLabel(option)}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label={label} size='small' fullWidth />
              )}
            />
          )}
        />
      </Grid>
    )
  }

  const renderTextField = (
    name: string,
    label: string,
    md = 2,
    tooltip?: string,
  ) => {
    if (hiddenFields.has(name)) return null

    return (
      <Grid item xs={12} md={md} pr={0.25} key={name}>
        <Controller
          control={control}
          name={name}
          render={({ field }) => {
            const textField = (
              <TextField {...field} value={field.value ?? ''} size='small' label={label} fullWidth />
            )
            return tooltip ? <Tooltip title={tooltip}>{textField}</Tooltip> : textField
          }}
        />
      </Grid>
    )
  }

  const renderDatePicker = (name: string, label: string, md = 2) => {
    if (hiddenFields.has(name)) return null

    return (
      <Grid item xs={12} md={md} pr={0.25} key={name}>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value } }) => (
            <DatePicker
              label={label}
              views={['year', 'month', 'day']}
              {...register(name)}
              renderInput={(params) => (
                <TextField {...params} error={false} fullWidth size='small' />
              )}
              value={value ? new Date(value) : null}
              onChange={(date) => {
                if (date && isValid(date)) {
                  onChange(date.toISOString())
                } else {
                  onChange(null)
                }
              }}
            />
          )}
        />
      </Grid>
    )
  }

  const renderYearPicker = (name: string, label: string) => {
    if (hiddenFields.has(name)) return null

    return (
      <Grid item xs={12} md={2} pr={0.25} key={name}>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value } }) => (
            <DatePicker
              label={label}
              views={['year']}
              {...register(name)}
              renderInput={(params) => (
                <TextField {...params} error={false} fullWidth size='small' />
              )}
              value={value ? new Date(value) : null}
              onChange={(date) => {
                if (date && isValid(date)) {
                  const yearString = date.getFullYear().toString()
                  if (yearString.length === 4) {
                    onChange(yearString)
                  }
                } else {
                  onChange(null)
                }
              }}
            />
          )}
        />
      </Grid>
    )
  }

  const renderRadioGroup = (
    name: string,
    label: string,
    options: { value: string; label: string }[],
    autoWidth = false,
  ) => {
    if (hiddenFields.has(name)) return null

    return (
      <Grid item xs={12} md={autoWidth ? 'auto' : 2} pr={0.25} key={name}>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value } }) => (
            <FormControl>
              <FormLabel>{label}</FormLabel>
              <RadioGroup
                row
                value={value || ''}
                onChange={(event) => onChange(event.target.value)}
              >
                {options.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size='small' />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        />
      </Grid>
    )
  }

  const renderStatusCheckboxes = () => {
    const showSkilaskylt = shouldShowField('skilaskyltEintak')
    const showFrumEintak = shouldShowField('frumEintak')

    if (!showSkilaskylt && !showFrumEintak) return null

    const skilaskyltValue = watch('skilaskyltEintak')
    const frumEintakValue = watch('frumEintak')

    return (
      <Grid
        item
        xs={12}
        md='auto'
        pr={0.25}
        alignSelf='flex-end'
        key='statusCheckboxes'
      >
        <FormControl>
          <RadioGroup row sx={{ gap: 0.5 }}>
            {showSkilaskylt && (
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    onChange={(event) => {
                      setValue(
                        'skilaskyltEintak',
                        event.target.checked ? '1' : null,
                      )
                    }}
                    checked={skilaskyltValue === '1'}
                  />
                }
                label='Skilaskylt'
              />
            )}
            {showFrumEintak && (
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    onChange={(event) => {
                      setValue('frumEintak', event.target.checked ? '1' : null)
                    }}
                    checked={frumEintakValue === '1'}
                  />
                }
                label='Frumeintak'
              />
            )}
          </RadioGroup>
        </FormControl>
      </Grid>
    )
  }

  const renderOriginNumber = () => {
    if (type === 'connectedItems' || type === 'itemLocations') return null
    if (hiddenFields.has('originNumberSelectItem')) return null

    return (
      <Grid item xs={12} md={2} pr={0.25} key='originNumberSelectItem'>
        <Controller
          control={control}
          name='originNumberSelectItem'
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              multiple
              size='small'
              options={normalizeSelectItems(originOptions || [])}
              getOptionLabel={getSelectItemOptionLabel}
              isOptionEqualToValue={isSelectItemEqual}
              onChange={(e, data) => {
                const next = normalizeSelectItems(data)
                onChange(next)
                formStateRef.current = {
                  ...formStateRef.current,
                  originNumberSelectItem: next,
                }
                userActionRef.current = true
                setTimeout(
                  () => handleOnSubmit(onFilter)(formStateRef.current),
                  0,
                )
              }}
              onInputChange={handleInputChangeOrigin}
              value={value || []}
              clearOnBlur={false}
              renderOption={(props, option) => (
                <li {...props} key={(option as SelectItem).id}>
                  {getSelectItemOptionLabel(option)}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Upprunar'
                  size='small'
                  fullWidth
                />
              )}
            />
          )}
        />
      </Grid>
    )
  }

  const renderItemCollectionNumber = () => {
    if (hiddenFields.has('itemCollectionNumberSelectItem')) return null

    return (
      <Grid item xs={12} md={2} pr={0.25} key='itemCollectionNumberSelectItem'>
        <Controller
          control={control}
          name='itemCollectionNumberSelectItem'
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              multiple
              size='small'
              options={normalizeSelectItems(itemCollectionOptions || [])}
              getOptionLabel={getSelectItemOptionLabel}
              isOptionEqualToValue={isSelectItemEqual}
              onChange={(e, data) => {
                const next = normalizeSelectItems(data)
                onChange(next)
                formStateRef.current = {
                  ...formStateRef.current,
                  itemCollectionNumberSelectItem: next,
                }
                userActionRef.current = true
                setTimeout(
                  () => handleOnSubmit(onFilter)(formStateRef.current),
                  0,
                )
              }}
              onInputChange={handleInputChangeItemCollection}
              value={value || []}
              clearOnBlur={false}
              renderOption={(props, option) => (
                <li {...props} key={(option as SelectItem).id}>
                  {getSelectItemOptionLabel(option)}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label='Aðföng' size='small' fullWidth />
              )}
            />
          )}
        />
      </Grid>
    )
  }

  const renderItemNumber = () => {
    if (hiddenFields.has('itemNumberSelectItem')) return null

    return (
      <Grid item xs={12} md={2} pr={0.5} key='itemNumberSelectItem'>
        <Controller
          control={control}
          name='itemNumberSelectItem'
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              multiple
              size='small'
              options={normalizeSelectItems(itemOptions || [])}
              getOptionLabel={getSelectItemOptionLabel}
              isOptionEqualToValue={isSelectItemEqual}
              onChange={(e, data) => {
                const next = normalizeSelectItems(data)
                onChange(next)
                formStateRef.current = {
                  ...formStateRef.current,
                  itemNumberSelectItem: next,
                }
                userActionRef.current = true
                setTimeout(
                  () => handleOnSubmit(onFilter)(formStateRef.current),
                  0,
                )
              }}
              onInputChange={handleInputChangeItem}
              value={value || []}
              clearOnBlur={false}
              renderOption={(props, option) => (
                <li {...props} key={(option as SelectItem).id}>
                  {getSelectItemOptionLabel(option)}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label='Eintök' size='small' fullWidth />
              )}
            />
          )}
        />
      </Grid>
    )
  }

  const renderUpprunalegirEigendur = () => {
    if (!shouldShowField('upprunalegirEigendurSelectItem')) return null

    return (
      <Grid item xs={12} md={2} pr={0.25} key='upprunalegirEigendurSelectItem'>
        <Controller
          control={control}
          name='upprunalegirEigendurSelectItem'
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              multiple
              size='small'
              options={normalizeSelectItems(customerOptions || [])}
              value={value || []}
              onChange={(_, newValue) => {
                const next = normalizeSelectItems(newValue)
                onChange(next)
                formStateRef.current = {
                  ...formStateRef.current,
                  upprunalegirEigendurSelectItem: next,
                }
                userActionRef.current = true
                setTimeout(
                  () => handleOnSubmit(onFilter)(formStateRef.current),
                  0,
                )
              }}
              onInputChange={handleInputChangeCustomer}
              inputValue={inputValueCustomer}
              clearOnBlur={false}
              filterOptions={filterOptions}
              getOptionLabel={getSelectItemOptionLabel}
              isOptionEqualToValue={isSelectItemEqual}
              renderOption={(props, option) => (
                <li {...props} key={(option as SelectItem).id}>
                  {getSelectItemOptionLabel(option)}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size='small'
                  label='Upprunalegir Eigendur'
                  fullWidth
                />
              )}
              onOpen={() => {
                if (customerOptions.length === 0) {
                  getCustomerOptions()
                }
              }}
            />
          )}
        />
      </Grid>
    )
  }

  return (
    <DateFnsProvider>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={1} alignContent={'flex-end'}>
          {renderOriginNumber()}
          {renderItemCollectionNumber()}
          {renderItemNumber()}
          {shouldShowField('formSelectItem') &&
            type === 'items' &&
            renderAutocomplete(
              'formSelectItem',
              'Tegund forms',
              formOptions || [],
              2,
            )}
          {shouldShowField('itemTypeSelectItem') &&
            (shouldShowField('itemOrderFrom') || type === 'items'
              ? renderAutocomplete(
                  'itemTypeSelectItem',
                  'Tegund eintaks',
                  allTypeOptions || [],
                  2,
                )
              : renderAutocomplete(
                  'itemTypeSelectItem',
                  'Tegund eintaks',
                  itemTypeOptions || [],
                  2,
                ))}
          {shouldShowField('heiti') && renderTextField('heiti', 'Heiti')}
          {shouldShowField('titleSelectItem') &&
            renderAutocomplete(
              'titleSelectItem',
              'Titill',
              titleOptions || [],
              2,
              inputValueTitle,
              handleInputChangeTitle,
              false,
            )}
          {shouldShowField('locationsSelectItem') &&
            renderAutocomplete(
              'locationsSelectItem',
              'Staðsetning',
              locationsOptions || [],
            )}
          {shouldShowField('coldLocationsSelectItem') &&
            renderAutocomplete(
              'coldLocationsSelectItem',
              'LTO staðsetning',
              coldLocationsOptions || [],
            )}
          {shouldShowField('languagesSelectItem') &&
            renderAutocomplete(
              'languagesSelectItem',
              'Tungumál',
              languagesOptions || [],
            )}
          {shouldShowField('nedanmalstextarSelectItem') &&
            renderAutocomplete(
              'nedanmalstextarSelectItem',
              'Neðanmálstextar',
              nedanmalstextarOptions || [],
            )}
          {shouldShowField('ownersNumber') &&
            renderTextField('ownersNumber', 'Númer eiganda')}
          {shouldShowField('breiddSelectItem') &&
            renderAutocomplete(
              'breiddSelectItem',
              'Breidd',
              breiddOptions || [],
            )}
          {shouldShowField('rammarASekundu') &&
            renderTextField('rammarASekundu', 'Rammar á sekúndu')}
          {shouldShowField('gbStaerd') &&
            renderTextField('gbStaerd', 'GB stærð')}
          {shouldShowField('staerd') && renderTextField('staerd', 'Stærð')}
          {shouldShowField('slod') && renderTextField('slod', 'Slóð')}
          {shouldShowField('upprunalegirEigendurSelectItem') &&
            renderUpprunalegirEigendur()}
          {shouldShowField('lysing') && renderTextField('lysing', 'Lýsing')}
          {shouldShowField('athugasemd') &&
            renderTextField('athugasemd', 'Athugasemdir')}
          {shouldShowField('minnispunktar') &&
            renderTextField(
              'minnispunktar',
              'Minnispunktar',
              2,
              'Leitaðu af mörgum orðum með því að setja , á milli orðanna.',
            )}
          {shouldShowField('lysingStafvaedingar') &&
            renderTextField('lysingStafvaedingar', 'Lýsing stafvæðingar')}
          {shouldShowField('lysingSafnmunar') &&
            renderTextField('lysingSafnmunar', 'Lýsing safnmunar')}
          {shouldShowField('upplysingarUmSafnmuninn') &&
            renderTextField(
              'upplysingarUmSafnmuninn',
              'Upplýsingar um safnmuninn',
            )}
          {shouldShowField('heimildSafnmunir') &&
            renderTextField('heimildSafnmunir', 'Heimildir um safnmuninn')}
          {shouldShowField('timabilEfnisFra') &&
            renderDatePicker('timabilEfnisFra', 'Tímabil efnis frá')}
          {shouldShowField('timabilEfnisTil') &&
            renderDatePicker('timabilEfnisTil', 'Tímabil efnis til')}
          {shouldShowField('year') && renderYearPicker('year', 'Upprunaár')}
          {shouldShowField('itemOrderFrom') &&
            renderTextField('itemOrderFrom', 'Eintakanúmer frá', 2)}
          {shouldShowField('itemOrderTo') &&
            renderTextField('itemOrderTo', 'Eintakanúmer til', 2)}
        </Grid>

        <Grid container spacing={1} sx={{ mt: 1 }}>
          {shouldShowField('stadaSkraningar') &&
            renderRadioGroup(
              'stadaSkraningar',
              'Staða skráningar',
              [
                { value: '', label: 'Öll' },
                { value: '1', label: 'Í vinnslu' },
                { value: '2', label: 'Lokið' },
              ],
              true,
            )}
          {shouldShowField('afritunLokid') &&
            renderRadioGroup(
              'afritunLokid',
              'Afritun lokið',
              [
                { value: '', label: 'Öll' },
                { value: '1', label: 'Já' },
                { value: '0', label: 'Nei' },
              ],
              true,
            )}
          {shouldShowField('buidAdFylla') &&
            renderRadioGroup(
              'buidAdFylla',
              'Búið að fylla LTO band',
              [
                { value: '', label: 'Öll' },
                { value: '1', label: 'Já' },
                { value: '0', label: 'Nei' },
              ],
              true,
            )}
          {shouldShowField('ltoGerd') &&
            renderRadioGroup(
              'ltoGerd',
              'Gerð',
              [
                { value: '', label: 'Öll' },
                { value: 'A', label: 'A' },
                { value: 'B', label: 'B' },
              ],
              true,
            )}
          {shouldShowField('stafvaeding') &&
            renderRadioGroup(
              'stafvaeding',
              'Stafvæðing skráningar',
              [
                { value: '', label: 'Öll' },
                { value: '1', label: 'Já' },
                { value: '2', label: 'Nei' },
                { value: '3', label: 'Nei, til á filmu' },
                { value: '4', label: 'Stafvæðingu lokið' },
              ],
              true,
            )}
          {renderStatusCheckboxes()}
        </Grid>
        <FilterControls
          selected={selected}
          clearFilters={clearFilters}
          register={register}
          deselectCheckboxChange={deselectCheckboxChange}
        />
      </form>
    </DateFnsProvider>
  )
}
